import { debounce } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import type { DocumentNode, TypedDocumentNode, DefaultContext } from "@apollo/client";
import type { MutationHookOptions } from "@apollo/client/react";

export const useDebounceMutation = (
  MUTATION: DocumentNode | TypedDocumentNode<any, any>,
  defaultContext: DefaultContext = {}
) => {
  const [mutate] = useMutation(MUTATION, { context: defaultContext });
  const debounceMutate = useMemo(() => debounce(mutate, 300), []);

  return [debounceMutate] as [typeof mutate];
};

interface TUseGqlFieldUtils {
  isDirty: boolean;
  isReady: boolean;
  /** Cancel any pending debounced mutation and reset to the given value */
  cancelAndReset: (resetValue: any) => void;
}
type UpdateFunct<T = any> = (t: T) => any;

export const useGqlField = <T extends any = any>(
  fieldValue: any,
  id: any,
  MUTATION: any,
  mutationHookOptions: MutationHookOptions = {}
) => {
  const [mutate] = useMutation(MUTATION, mutationHookOptions);
  // Create debounced function and keep a stable reference
  const debouncedMutateRef = useRef(debounce(mutate, 300));
  // Update the debounced function if mutate changes
  useEffect(() => {
    debouncedMutateRef.current = debounce(mutate, 300);
  }, [mutate]);
  
  const [value, setValue] = useState(fieldValue);
  const [valueInitilized, setValueInitialized] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isReady, setIsReady] = useState(false);
  // Track if we're in "cancelled" state to skip the next mutation trigger
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (typeof fieldValue !== undefined && value !== "") {
      setValueInitialized(true);
    }
  }, [value]);
  useEffect(() => {
    if (value === fieldValue) {
      setIsDirty(false);
    }
  }, [value, fieldValue]);

  useEffect(() => {
    // Skip mutation if we just cancelled
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const idMissing =
      id === null ||
      id === undefined ||
      (typeof id === "string" && id.trim() === "");
    if (
      !idMissing &&
      value !== fieldValue &&
      typeof fieldValue !== undefined &&
      value !== ""
    ) {
      setIsDirty(true);
      debouncedMutateRef.current({ variables: { id, value } });
    }
  }, [value]);

  useEffect(() => {
    if (!isDirty) {
      if (typeof fieldValue === "boolean") {
        setValue(fieldValue);
      } else {
        setValue(fieldValue || "");
      }
    }
  }, [fieldValue || ""]);
  useEffect(() => {
    if (value === fieldValue && typeof fieldValue !== "undefined") {
      setIsReady(true);
    }
    if (typeof fieldValue === "undefined") {
      setIsReady(false);
    }
  }, [value, fieldValue]);

  // Cancel pending debounced mutation and reset local state
  const cancelAndReset = useCallback((resetValue: any) => {
    // Cancel any pending debounced call
    debouncedMutateRef.current.clear();
    // Mark as cancelled so the setValue effect doesn't trigger a new mutation
    cancelledRef.current = true;
    // Reset local state
    setIsDirty(false);
    setValue(resetValue);
  }, []);

  return [value, setValue, { isDirty, isReady, cancelAndReset }] as [
    T,
    UpdateFunct<T>,
    TUseGqlFieldUtils
  ];
};
